import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import LandingBackgroundFX from "@/components/LandingBackgroundFX";
import { LP_CSS, LOGO } from "@/pages/LandingPage";

const WHATSAPP_NUMBER = "5511981429962";
const WHATSAPP_MSG = encodeURIComponent(
  "Olá! Vi a página do CRM do AutoPostBR e quero saber mais — pode me apresentar a ferramenta ou marcar uma vídeo chamada?"
);
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`;

const BENEFICIOS = [
  {
    icon: "🗂️",
    title: "Kanban comercial + backoffice",
    desc: "Do primeiro contato até a instalação: Lead → Contato → Proposta → Fechado → Enviado BKO → Para Correção / Venda Perdida / Concluído. Tudo num só quadro, sem planilha paralela.",
  },
  {
    icon: "👥",
    title: "Contas de equipe com papéis",
    desc: "Cadastre vendedores, backoffice, coordenadores e supervisores. Vendedor só vê os próprios negócios; quem administra vê a operação inteira.",
  },
  {
    icon: "🔒",
    title: "Controle de quem pode concluir",
    desc: "Só backoffice/coordenador confirma um pedido como concluído — o vendedor não se autodeclara, evitando erro e fraude.",
  },
  {
    icon: "📎",
    title: "Anexos em cada card",
    desc: "Checklist assinado, print de conversa, comprovante, contrato em PDF — tudo junto do negócio, guardado com segurança.",
  },
  {
    icon: "🕓",
    title: "Histórico completo de movimentação",
    desc: "Quem moveu, quando, de onde pra onde e por quê. Auditoria pronta pra qualquer pedido, sem precisar perguntar pro time.",
  },
  {
    icon: "🔔",
    title: "Notificação de quem mexeu no pedido",
    desc: "Alguém moveu um card da sua equipe? Você fica sabendo direto no painel, sem precisar ficar atualizando a tela toda hora.",
  },
  {
    icon: "💬",
    title: "Já nasce integrado ao WhatsApp",
    desc: "O mesmo painel que já automatiza sua prospecção e atendimento — sem contratar outra ferramenta separada só pro funil comercial.",
  },
  {
    icon: "🔢",
    title: "Numeração de pedido",
    desc: "Cada card recebe um número sequencial — acha o pedido certo na hora, mesmo quando o mesmo cliente aparece mais de uma vez.",
  },
];

export default function CrmLandingPage() {
  const navigate = useNavigate();
  const goLogin = () => navigate("/login");
  const mockupRef = useRef(null);

  return (
    <div className="lp-root">
      <style>{LP_CSS}</style>
      <style>{`
        .crm-accent { color: #c084fc; }
        .crm-grad { background: linear-gradient(115deg,#c084fc,#a855f7 55%,#fff); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; }
        .crm-hero { position:relative; z-index:10; max-width:900px; margin:0 auto; padding:9rem 5% 4rem; text-align:center; }
        .crm-badge { display:inline-flex; align-items:center; gap:8px; padding:.5rem 1.1rem; border-radius:100px; background:rgba(168,85,247,0.12); border:1px solid rgba(168,85,247,0.35); font-size:.78rem; font-weight:700; letter-spacing:.5px; text-transform:uppercase; color:#d8b4fe; margin-bottom:1.6rem; }
        .crm-hero h1 { font-size:clamp(2.3rem,5vw,3.6rem); line-height:1.08; font-weight:800; letter-spacing:-.03em; margin:0 0 1.3rem; }
        .crm-hero p { font-size:1.08rem; line-height:1.6; color:var(--lp-muted); max-width:620px; margin:0 auto 2.2rem; }
        .crm-actions { display:flex; gap:1rem; align-items:center; justify-content:center; flex-wrap:wrap; }
        .crm-btn-wa { background:#22c55e; color:#fff; border:none; padding:.9rem 1.8rem; border-radius:100px; font-weight:700; font-size:.92rem; cursor:pointer; display:inline-flex; align-items:center; gap:8px; text-decoration:none; box-shadow:0 8px 24px rgba(34,197,94,.4); transition:transform .25s ease, box-shadow .25s ease; }
        .crm-btn-wa:hover { transform:translateY(-2px); box-shadow:0 12px 30px rgba(34,197,94,.55); }
        .crm-btn-ghost { background:rgba(255,255,255,.06); border:1px solid var(--lp-border); color:var(--lp-text); padding:.9rem 1.8rem; border-radius:100px; font-weight:600; font-size:.92rem; cursor:pointer; }
        .crm-grid { position:relative; z-index:10; max-width:1200px; margin:2rem auto 0; padding:0 5% 5rem; display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:1.2rem; }
        .crm-card { background:var(--lp-glass); border:1px solid var(--lp-border); border-radius:22px; padding:1.8rem; backdrop-filter:blur(10px); transition:transform .3s ease, border-color .3s ease; }
        .crm-card:hover { transform:translateY(-6px); border-color:rgba(168,85,247,.5); }
        .crm-card .ico { width:50px; height:50px; border-radius:14px; background:rgba(168,85,247,.16); border:1px solid rgba(168,85,247,.35); display:grid; place-items:center; margin-bottom:1.2rem; font-size:1.4rem; }
        .crm-card h3 { font-size:1.08rem; font-weight:700; margin:0 0 .5rem; }
        .crm-card p { color:var(--lp-muted); font-size:.9rem; line-height:1.55; margin:0; }
        .crm-cta-box { position:relative; z-index:10; max-width:900px; margin:0 auto 6rem; padding:3.2rem 5%; text-align:center; background:linear-gradient(160deg,rgba(168,85,247,.14),rgba(255,255,255,.02)); border:1px solid rgba(168,85,247,.3); border-radius:28px; }
        .crm-cta-box h2 { font-size:clamp(1.7rem,3vw,2.3rem); font-weight:800; margin:0 0 .8rem; }
        .crm-cta-box p { color:var(--lp-muted); margin:0 0 2rem; font-size:1rem; }
        .crm-beta-tag { display:inline-block; margin-left:10px; font-size:.7rem; font-weight:800; color:#d8b4fe; background:rgba(168,85,247,.15); border:1px solid rgba(168,85,247,.35); padding:.2rem .55rem; border-radius:100px; vertical-align:middle; }
      `}</style>

      <LandingBackgroundFX chipData={[]} mockupRef={mockupRef}>

        <header className="lp-header">
          <div className="lp-brand" style={{ cursor: "pointer" }} onClick={() => navigate("/")}>
            <img src={LOGO} alt="AutoPost BR" className="lp-logo" />
            <span>AutoPostBR <span className="crm-accent">CRM</span></span>
          </div>
          <button className="lp-cta-top" style={{ background: "#9333ea", boxShadow: "0 8px 24px rgba(147,51,234,.4)" }} onClick={goLogin}>
            Acessar painel
          </button>
        </header>

        <section className="crm-hero">
          <div className="crm-badge">🇧🇷 Novo recurso — Pipeline de Empresa<span className="crm-beta-tag">BETA</span></div>
          <h1>O <span className="crm-grad">CRM</span> que já nasce dentro do WhatsApp que vende sozinho.</h1>
          <p>
            Não é só automação — é gestão de equipe de verdade. Kanban comercial e de backoffice,
            contas de vendedor e coordenador, histórico de cada negócio e anexos, tudo no mesmo
            painel que já cuida da sua prospecção e atendimento.
          </p>
          <div className="crm-actions">
            <a className="crm-btn-wa" href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
               Falar no WhatsApp e marcar uma demonstração
            </a>
            <button className="crm-btn-ghost" onClick={goLogin}>Já sou cliente</button>
          </div>
        </section>

        <section className="crm-grid">
          {BENEFICIOS.map((b, i) => (
            <div className="crm-card" key={i}>
              <div className="ico">{b.icon}</div>
              <h3>{b.title}</h3>
              <p>{b.desc}</p>
            </div>
          ))}
        </section>

        <section className="crm-cta-box">
          <h2>Bora conhecer o CRM de perto?</h2>
          <p>
            Te mostro a ferramenta funcionando de verdade — pode ser uma conversa rápida ou uma
            vídeo chamada, você escolhe. Fale direto comigo pelo WhatsApp.
          </p>
          <a className="crm-btn-wa" href={WHATSAPP_LINK} target="_blank" rel="noreferrer">
             (11) 98142-9962 — Chamar no WhatsApp
          </a>
        </section>

      </LandingBackgroundFX>
    </div>
  );
}
